/**
 * IS THE SAVED FILTER STILL WHAT IS ON SCREEN? (KPR-1642, KPE-051 rulings
 * i02 and i11.) The items page names the applied saved filter in its menu and
 * shows a dot when what is on screen has moved away from it: the conditions,
 * the sort levels, the group period, the card, the columns, the view mode or
 * the board. Undoing the change (the screen equal to the filter again) takes
 * the dot away. This file is that comparison, pure.
 *
 * Both sides are written in the ADDRESS's own terms (utils/itemsView
 * `ITEMS_VIEW_PARAMS`), because the address is where every one of those parts
 * lives and what applying a filter writes: `q`, `card`, `view`, `cols`,
 * `board`, `lanes`, `sort` … `dir3` and `group`. A missing value reads as its
 * default, so `sort` absent and `sort=primaryDate` are one state.
 *
 * The BASELINE is the filter's stored parts laid over the state as it stood
 * when the filter was applied (the anchor). A part the filter does not store
 * (an older filter's card or view, a filter saved with no grouping) is left as
 * it was when the filter was applied, because applying it leaves that part
 * alone; changing it from there is a change too.
 */

import { PRIMARY_DATE_SORT } from './cards';
import { DEFAULT_GROUP_TOKEN, isGroupToken } from './itemGroups';
import { canonicalize } from './kqlBuilder';
import { sortThenOf } from './sortLevels';
import { columnsFromParam, columnsParamOf, savedFilterViewPayload } from './savedFilterView';
import { savedFilterApplyPatch } from './savedFilters';

/** The parts a change is reported by, in the order a person reads them. */
export const SAVED_FILTER_PARTS = Object.freeze([
	{ key: 'conditions', label: 'the conditions', params: ['q'] },
	{ key: 'sort', label: 'the sort', params: ['sort', 'dir', 'sort2', 'dir2', 'sort3', 'dir3'] },
	{ key: 'group', label: 'the group', params: ['group'] },
	{ key: 'card', label: 'the card', params: ['card'] },
	{ key: 'columns', label: 'the columns', params: ['cols'] },
	{ key: 'view', label: 'the view mode', params: ['view'] },
	{ key: 'board', label: 'the board', params: ['board', 'lanes'] },
]);

const PARAMS = SAVED_FILTER_PARTS.flatMap(part => part.params);
const text = value => (typeof value === 'string' ? value.trim() : '');
const dirOf = value => (text(value).toLowerCase() === 'asc' ? 'asc' : 'desc');

/** A filter's text as the comparison reads it: canonical where it parses, else as typed. */
export const canonicalText = value => {
	const raw = text(value);
	if (!raw) return '';
	return canonicalize(raw) || raw;
};

/**
 * Any address-shaped object as the comparison reads it: every param named,
 * blanks as '', the sort's defaults filled in, a further level's direction
 * only beside its field, the columns normalised (utils/savedFilterView).
 */
export function normalizeSavedFilterState(state = {}) {
	const [two, three] = sortThenOf([
		{ field: state.sort2, direction: state.dir2 },
		{ field: state.sort3, direction: state.dir3 },
	]);
	const group = text(state.group);
	return {
		q: canonicalText(state.q),
		sort: text(state.sort) || PRIMARY_DATE_SORT,
		dir: dirOf(state.dir),
		sort2: two?.field || '',
		dir2: two ? two.direction : '',
		sort3: three?.field || '',
		dir3: three ? three.direction : '',
		group: isGroupToken(group) ? group : DEFAULT_GROUP_TOKEN,
		card: text(state.card).toLowerCase(),
		cols: columnsParamOf(columnsFromParam(text(state.cols))) || '',
		view: text(state.view),
		board: text(state.board),
		lanes: text(state.board) ? text(state.lanes) : '',
	};
}

/**
 * What the items page shows, as the comparison reads it. Each input is the
 * page's own CHOICE, read synchronously from the address or its stored
 * preference, never a value that settles later (a table layout's order, a
 * board that cannot be drawn yet), so the dot never flickers while the page
 * loads.
 */
export function savedFilterStateOf({
	query = '',
	cardFilter = '',
	viewMode = '',
	savedColumns = null,
	sortField = '',
	sortDirection = '',
	sortThen = null,
	groupToken = '',
	board = null,
} = {}) {
	const levels = sortThenOf(sortThen);
	return normalizeSavedFilterState({
		q: query,
		card: cardFilter,
		view: viewMode,
		cols: columnsParamOf(savedColumns) || '',
		sort: sortField,
		dir: sortDirection,
		sort2: levels[0]?.field,
		dir2: levels[0]?.direction,
		sort3: levels[1]?.field,
		dir3: levels[1]?.direction,
		group: groupToken,
		board: board?.by || '',
		lanes: board?.lanes || '',
	});
}

/**
 * The address patch that applies a saved filter from the menu: the one patch
 * `savedFilterApplyPatch` builds (query, card, view, columns, board, sort,
 * group), plus which filter it is (`saved`). A filter that stores no columns
 * clears `cols`, so the filter applied before it does not leave its columns
 * behind.
 */
export function savedFilterMenuPatch(filter, id) {
	const patch = savedFilterApplyPatch(filter);
	return { ...patch, cols: patch.cols || undefined, saved: id || undefined };
}

/**
 * The filter's stored parts over the anchor (the state when it was applied):
 * what the screen shows while nothing has been changed since.
 */
export function savedFilterBaseline(filter, anchor) {
	const patch = savedFilterMenuPatch(filter);
	const stored = {};
	PARAMS.forEach((param) => {
		if (Object.prototype.hasOwnProperty.call(patch, param)) stored[param] = patch[param];
	});
	// A filter that stores an order stores all of it: its further levels
	// replace the address's (sortThenPatch names all four), and a field it
	// leaves unset keeps the anchor's.
	return normalizeSavedFilterState({ ...(anchor || {}), ...stored });
}

/**
 * The parts of `state` that differ from the filter's `baseline`, as keys of
 * SAVED_FILTER_PARTS, in order ([] for none). The conditions also match what
 * the list adopted the stored text as (`spellings`, canonical): a filter
 * saved before keepr named things by id is still the filter on screen once
 * the list has rewritten it.
 */
export function savedFilterChanges(state, baseline, { spellings = [] } = {}) {
	if (!state || !baseline) return [];
	const same = new Set(spellings.map(canonicalText).filter(Boolean));
	return SAVED_FILTER_PARTS.filter(part => part.params.some((param) => {
		if (param === 'q' && same.has(state.q)) return false;
		return state[param] !== baseline[param];
	})).map(part => part.key);
}

/** "the conditions and the sort", for the dot's words. */
export function describeSavedFilterChanges(keys = []) {
	const labels = SAVED_FILTER_PARTS.filter(part => keys.includes(part.key)).map(part => part.label);
	if (labels.length <= 1) return labels[0] || '';
	return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

/** With nothing applied, there is something to save: a condition, or an order someone chose (not the default). */
export function hasSomethingToSave(state) {
	if (!state) return false;
	return Boolean(state.q)
		|| state.sort !== PRIMARY_DATE_SORT
		|| state.dir !== 'desc'
		|| Boolean(state.sort2);
}

/** The address patch that puts the screen back to `state` (Revert): every part named. */
export function savedFilterStatePatch(state) {
	const patch = {};
	PARAMS.forEach((param) => { patch[param] = state?.[param] || undefined; });
	return patch;
}

/**
 * What a save sends (Save filter, Save as new) and what Update writes: the
 * whole of what is on screen, so the filter afterwards IS the screen.
 *
 * - `query`: the filter text;
 * - the order on screen (`sortField`, `sortDirection`, `sortThen`: what the
 *   feed is sorted by, a table layout's own order included);
 * - `group_by`: the period, where grouping applies (else null: none);
 * - `board`: `{ by, lanes? }` while the list is a board (else null);
 * - `card`, `view`, `columns` (utils/savedFilterView `savedFilterViewPayload`):
 *   the applied filter's own columns while they stand in (`savedColumns`),
 *   else the columns the table of one card draws (`tableColumns`), else none.
 */
export function savedFilterDraft({
	query = '',
	sortField = null,
	sortDirection = null,
	sortThen = null,
	groupBy = null,
	board = null,
	cardFilter = null,
	viewMode = null,
	savedColumns = null,
	tableColumns = null,
	elementDefs = null,
} = {}) {
	const view = savedFilterViewPayload({ card: cardFilter, view: viewMode, tableColumns, elementDefs });
	const own = columnsFromParam(columnsParamOf(savedColumns) || '');
	return {
		query: text(query),
		sort_field: sortField || null,
		sort_direction: sortField ? dirOf(sortDirection) : null,
		sort_then: sortThenOf(sortThen),
		group_by: isGroupToken(groupBy) ? groupBy : null,
		board: board?.by ? (board.lanes ? { by: board.by, lanes: board.lanes } : { by: board.by }) : null,
		...view,
		columns: own.length ? own : view.columns,
	};
}
